
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  ClearRounded,
  EditRounded,
  LocalPrintshopRounded,
  VisibilityRounded,
} from "@mui/icons-material";

import {
  Box,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";

import { Alert, Dialog, Table } from "../../layout";

import * as actions from "../../api/actions/pos";
import { endpoints } from "../../api/actions/api";

import { AppContext } from "../../App";
import { safeOnComplete } from "../../utils/safeOnComplete";

import ReportPrintButton from "../reports/ReportPrintButton";

//------------------------------------------------
// Reusable Store Details Content
//------------------------------------------------
const StoreDetailsContent = ({ store, t }) => {
  if (!store) return null;

  const managerName = [
    store.firstName,
    store.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  const details = [
    [t("storeName"), store.storeName],
    [t("managerName"), managerName],
    [t("address"), store.address],
    [t("city"), store.city],
    [t("state"), store.state],
    [t("zip"), store.zip],
    [t("phone"), store.phone],
  ];

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          mb: 3,
        }}
      >
        {t("storeDetails")}
      </Typography>

      <TableContainer>
        <MuiTable size="small">
          <TableBody>
            {details.map(([label, value], index) => (
              <TableRow key={index}>
                <TableCell
                  sx={{
                    width: "40%",
                    fontWeight: "bold",
                  }}
                >
                  {label}
                </TableCell>

                <TableCell>
                  {value ?? ""}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </Box>
  );
};

//------------------------------------------------
// Stores
//------------------------------------------------
const Stores = (props) => {
  const { t } = useTranslation();

  const {
    _stores,
    _get,
    _delete,
    _clear,
    _store,
  } = props;

  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);
  const detailsRef = useRef(null);

  const [showView, setShowView] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  //------------------------------------------------
  // Load Stores
  //------------------------------------------------
  useEffect(() => {
    setLoader(true);

    _get(
      endpoints.Store,
      actions.ActionType.Store,
      () => setLoader(false)
    );

    return () => {
      _clear();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //------------------------------------------------
  // Stores Table Data
  //------------------------------------------------
  const tableData = useMemo(() => {
    if (!Array.isArray(_stores)) {
      return [];
    }

    return _stores.map((obj) => ({
      ...obj,

      managerName: [
        obj.firstName,
        obj.lastName,
      ]
        .filter(Boolean)
        .join(" "),
    }));
  }, [_stores]);

  //------------------------------------------------
  // Add / Edit Store
  //------------------------------------------------
  const handleAddEdit = (store = null) => {
    navigate(
      store?.id
        ? `add-store-form/${store.id}`
        : "add-store-form"
    );
  };

  //------------------------------------------------
  // View Store
  //------------------------------------------------
  const handleView = (data) => {
    const original = Array.isArray(_stores)
      ? _stores.find(
          (store) =>
            String(store.id) === String(data.id)
        )
      : null;

    setSelectedStore(original || data);
    setShowView(true);
  };

  const handleCloseView = () => {
    setShowView(false);
    setSelectedStore(null);
  };

  //------------------------------------------------
  // Delete Store
  //------------------------------------------------
  const handleDlt = (id) => {
    if (String(_store?.id) === String(id)) {
      setAlert({
        isOpen: true,
        type: "warning",
        title: t("switchStoreWarning"),
        subTitle: t("switchStoreMessage"),
      });

      return;
    }

    setAlert({
      isOpen: true,
      type: "confirmation",
      title: t("dangerousDelete"),
      subTitle: t("deleteStoreConfirmation"),

      onConfirm: () => {
        const onComplete = safeOnComplete(
          (res) => {
            setAlert({
              isOpen: true,
              type:
                res.status === 200
                  ? "success"
                  : "error",
              title:
                res.status === 200
                  ? t("deletedSuccessfully")
                  : t("error"),
              subTitle: res.msg,
            });
          },
          (err) => {
            setAlert({
              isOpen: true,
              type: "error",
              title: t("error"),
              subTitle: err.msg,
            });
          }
        );

        _delete(
          endpoints.Store,
          id,
          actions.ActionType.RmvStore,
          onComplete
        );
      },
    });
  };

  //------------------------------------------------
  // Table Columns
  //------------------------------------------------
  const screenColumns = [
    { id: "actions", label: t("actions") },
    { id: "storeName", label: t("storeName") },
    { id: "managerName", label: t("managerName") },
    { id: "address", label: t("address") },
    { id: "city", label: t("city") },
    { id: "state", label: t("state") },
    { id: "zip", label: t("zip") },
    { id: "phone", label: t("phone") },
  ];

  const printColumns = screenColumns.filter(
    (column) => column.id !== "actions"
  );

  //------------------------------------------------
  // Action Buttons
  //------------------------------------------------
  const ActionComp = (data) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {/* View */}
      <Tooltip arrow title={t("view")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary mr-5"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Edit */}
      <Tooltip arrow title={t("edit")}>
        <IconButton
          onClick={() => handleAddEdit(data)}
          size="small"
          className="btn-primary mr-5"
        >
          <EditRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Printer Settings */}
      <Tooltip arrow title={t("printerSettings")}>
        <IconButton
          onClick={() =>
            navigate(`printers/${data.id}`)
          }
          size="small"
          className="btn-secondary mr-5"
        >
          <LocalPrintshopRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Delete */}
      <Tooltip arrow title={t("delete")}>
        <IconButton
          onClick={() => handleDlt(data.id)}
          size="small"
          className="btn-error"
        >
          <ClearRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  //------------------------------------------------
  // Add Store Button
  //------------------------------------------------
  const HeaderComp = () => (
    <Button
      onClick={() => handleAddEdit()}
      className="btn-secondary"
      sx={{
        width: { md: "180px", xs: "100%" },
        mb: { xs: "10px", md: "0px" },
        fontSize: "17px",
        padding: "8px",
      }}
    >
      {t("addStore")}
    </Button>
  );

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <>
      {/* Header and Print Preview */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("stores")}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("stores")}
        />
      </Box>

      {/* Normal Stores Table */}
      <Table
        ActionComp={ActionComp}
        HeaderComp={HeaderComp}
        tblHead={screenColumns}
        tblData={tableData}
      />

      {/* Printable Stores List */}
      <Box
        ref={reportRef}
        className="stores-list-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {t("stores")}
        </Typography>

        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* View Store Details Dialog */}
      <Dialog
        show={showView}
        maxWidth="sm"
        onClose={handleCloseView}
      >
        {selectedStore && (
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 2,
              }}
            >
              <ReportPrintButton
                reportRef={detailsRef}
                paperSize="Letter"
                reportTitle={t("storeDetails")}
              />
            </Box>

            <StoreDetailsContent
              store={selectedStore}
              t={t}
            />

            <Grid
              container
              justifyContent="center"
              sx={{ mt: 4 }}
            >
              <Button
                onClick={handleCloseView}
                className="btn-error"
                size="large"
                sx={{ minWidth: "150px" }}
              >
                {t("close")}
              </Button>
            </Grid>
          </Box>
        )}
      </Dialog>

      {/* Printable Store Details */}
      {selectedStore && (
        <Box
          ref={detailsRef}
          className="store-details-print"
          sx={{
            position: "absolute",
            left: "-10000px",
            top: 0,
            width: "1000px",
            backgroundColor: "#fff",
            color: "#000",
            p: 2,
          }}
        >
          <StoreDetailsContent
            store={selectedStore}
            t={t}
          />
        </Box>
      )}

      {/* Alert */}
      <Alert
        alert={alert}
        setAlert={setAlert}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  _stores: state.pos.stores,
  _type: state.pos.type,
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _get: actions.get,
  _delete: actions.Delete,
  _clear: actions.clear,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(Stores);
